import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts'
import { PATH_COLORS } from '../palette'

interface Series {
  label: string
  color?: string
  data: { year: number; value: number }[]
}

interface Props {
  series: Series[]
  height?: number
}

const fmt = (n: number) => (n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`)

// Pivot: each year is a row with one column per series so recharts can draw multiple lines from a single dataset
function pivot(series: Series[]) {
  const yearSet = new Set<number>()
  series.forEach((s) => s.data.forEach((d) => yearSet.add(d.year)))
  const years = [...yearSet].sort((a, b) => a - b)
  return years.map((year) => {
    const row: Record<string, number> = { year }
    series.forEach((s) => {
      const point = s.data.find((d) => d.year === year)
      row[s.label] = point?.value ?? 0
    })
    return row
  })
}

export default function TrajectoryLines({ series, height = 360 }: Props) {
  const data = pivot(series)
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 16, right: 24, bottom: 80, left: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#dcd7cb" />
        <XAxis
          dataKey="year"
          tick={{ fontSize: 11, fill: '#5a5a5a' }}
          label={{ value: 'Year', position: 'bottom', offset: 0, fontSize: 11, fill: '#5a5a5a' }}
        />
        <YAxis tickFormatter={fmt} tick={{ fontSize: 11, fill: '#5a5a5a' }} />
        <Tooltip formatter={(v) => fmt(Number(v))} />
        <Legend
          verticalAlign="bottom"
          align="center"
          wrapperStyle={{
            fontSize: 12,
            paddingTop: 36,
            lineHeight: 1.6,
            whiteSpace: 'normal',
            maxWidth: '100%',
          }}
        />
        {series.map((s, i) => (
          <Line
            key={s.label}
            type="monotone"
            dataKey={s.label}
            stroke={s.color || PATH_COLORS[i % PATH_COLORS.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}
